import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, useAuth, formatApiError } from "@/lib/auth";
import { useLabel, useLayout, Section } from "@/lib/dynamicEngine";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import {
  LogOut, Inbox, DollarSign, Search, ExternalLink, Loader2, Layers, KeyRound, Users, MessageSquare, Wallet, UserSquare, FolderOpen,
  Trash2, Archive, ArchiveRestore, Edit3, ArrowUp, ArrowDown, X, ChevronDown, BarChart3, Type,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "text-cyan-300 border-cyan-500/40 bg-cyan-500/5",
  reviewed: "text-nex-silver border-white/20 bg-white/5",
  accepted: "text-nex-gold border-nex-gold/50 bg-nex-gold/5",
  paid: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
  rejected: "text-red-400 border-red-500/40 bg-red-500/5",
};
const FILTERS = ["all", "new", "reviewed", "accepted", "paid", "rejected"];
const EDITABLE_STATUSES = ["new", "reviewed", "accepted", "paid", "rejected"];

// Hover-drop-down nav group. Keeps the top bar clean by consolidating
// financial + reporting surfaces under a single "Reports" trigger.
const ReportsMenu = () => {
  const [open, setOpen] = useState(false);
  const items = [
    { to: "/admin/finance",   label: "Finance",    icon: Wallet,     testid: "nav-report-finance",  desc: "Bank sync · reserves · distributions" },
    { to: "/admin/billing",   label: "Billing",    icon: DollarSign, testid: "nav-report-billing",  desc: "Credits ledger · burn rate" },
    { to: "/admin/documents", label: "Doc Center", icon: FolderOpen, testid: "nav-report-docs",     desc: "Invoices · statements · files" },
  ];
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid="nav-reports"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="mono-body text-[11px] uppercase tracking-[0.2em] text-nex-chrome hover:text-nex-gold flex items-center gap-1"
      >
        <BarChart3 size={12} /> Reports
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180 text-nex-gold" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full pt-2 z-40"
          data-testid="reports-menu"
        >
          <div className="w-64 card-industrial border border-nex-border/70 shadow-xl divide-y divide-nex-border/40">
            {items.map(({ to, label, icon: Icon, testid, desc }) => (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                onClick={() => setOpen(false)}
                className="flex items-start gap-3 px-4 py-3 hover:bg-nex-gold/10 transition-colors group"
              >
                <Icon size={16} className="text-nex-gold mt-0.5 shrink-0 group-hover:scale-110 transition-transform" />
                <div className="min-w-0">
                  <div className="mono-body text-[11px] uppercase tracking-[0.2em] text-nex-silver group-hover:text-nex-gold">{label}</div>
                  <div className="mono-body text-[10px] text-nex-muted mt-0.5">{desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sortKey, setSortKey] = useState("submitted_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null); // brief being edited
  const [busy, setBusy] = useState(false);

  // Dynamic Engine — labels + layout
  const heroLabel = useLabel("admin_dashboard.hero.title", "Every lead, every stack, every friction.");
  const heroKicker = useLabel("admin_dashboard.hero.kicker", "// Engine Briefs · Owner Dashboard");
  const searchPlaceholder = useLabel("admin_dashboard.search.placeholder", "Search by business, name, email, industry...");
  useLayout("admin_dashboard", [
    { id: "kpi_strip",    order: 0 },
    { id: "filters_bar",  order: 1 },
    { id: "bulk_actions", order: 2 },
    { id: "leads_table",  order: 3 },
  ]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/briefs?include_archived=${showArchived}`);
      setItems(data.items || []);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showArchived]);

  // Prune selection whenever the filter/search changes so bulk actions
  // never touch a lead that isn't currently visible.
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(filtered.map((b) => b.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filter, search, showArchived, items]);

  const filtered = useMemo(() => {
    let out = items;
    if (filter !== "all") out = out.filter((x) => (x.status || "new") === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((x) =>
        x.business_name?.toLowerCase().includes(q) ||
        x.full_name?.toLowerCase().includes(q) ||
        x.email?.toLowerCase().includes(q) ||
        x.industry?.toLowerCase().includes(q)
      );
    }
    out = [...out].sort((a, b) => {
      let va, vb;
      if (sortKey === "friction_score") { va = a.friction_score || 0; vb = b.friction_score || 0; }
      else { va = new Date(a.submitted_at).getTime(); vb = new Date(b.submitted_at).getTime(); }
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return out;
  }, [items, filter, search, sortKey, sortDir]);

  const stats = useMemo(() => {
    const s = { total: items.length, new: 0, accepted: 0, paid: 0, rejected: 0 };
    items.forEach((x) => { const st = x.status || "new"; if (s[st] !== undefined) s[st]++; });
    return s;
  }, [items]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };
  const sortIcon = (k) => sortKey !== k ? null : (sortDir === "asc" ? <ArrowUp size={11} className="inline ml-1" /> : <ArrowDown size={11} className="inline ml-1" />);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleSelectAll = () => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((b) => b.id)));

  const deleteOne = async (b) => {
    if (!window.confirm(`Delete "${b.business_name}"? This cannot be undone.`)) return;
    try { await apiClient.delete(`/admin/briefs/${b.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const archiveOne = async (b) => {
    try {
      await apiClient.post(`/admin/briefs/${b.id}/${b.archived ? "unarchive" : "archive"}`);
      toast.success(b.archived ? "Restored" : "Archived");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} lead${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const r = await apiClient.post("/admin/briefs/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const bulkArchive = async () => {
    if (selected.size === 0) return;
    try {
      const r = await apiClient.post("/admin/briefs/bulk-archive", { ids: Array.from(selected) });
      toast.success(`Archived ${r.data.archived}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.patch(`/admin/briefs/${editing.id}`, {
        business_name: editing.business_name,
        full_name: editing.full_name,
        email: editing.email,
        status: editing.status,
      });
      toast.success("Lead updated");
      setEditing(null);
      await load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); toast.success("Signed out."); };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <div className="border-b border-white/10 bg-[#0A0E17] sticky top-0 z-40 backdrop-blur-xl">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-4">
            <img src={LIFE_LOGO} alt="" className="h-9 w-9 object-contain" />
            <div className="w-px h-8 bg-nex-border" />
            <div className="flex items-center gap-3">
              <NexWordmark size="text-xl" />
              <span className="mono-body text-[10px] tracking-[0.25em] uppercase text-nex-gold">// Owner Console</span>
            </div>
          </Link>
          <div className="flex items-center gap-3 md:gap-4">
            <Link to="/admin/previews" data-testid="nav-previews" className="hidden md:inline-flex items-center mono-body text-[11px] uppercase tracking-[0.2em] text-nex-chrome hover:text-nex-gold"><Layers size={12} className="inline mr-1" /> Previews</Link>
            <Link to="/admin/users" data-testid="nav-staff" className="hidden md:inline-flex items-center mono-body text-[11px] uppercase tracking-[0.2em] text-nex-chrome hover:text-nex-gold"><Users size={12} className="inline mr-1" /> Staff</Link>
            <Link to="/admin/contacts" data-testid="nav-contacts" className="hidden md:inline-flex items-center mono-body text-[11px] uppercase tracking-[0.2em] text-nex-chrome hover:text-nex-gold"><UserSquare size={12} className="inline mr-1" /> CRM</Link>
            <div className="hidden md:block"><ReportsMenu /></div>
            <Link to="/admin/sms" data-testid="nav-sms" className="hidden lg:inline-flex items-center mono-body text-[11px] uppercase tracking-[0.2em] text-nex-chrome hover:text-nex-gold"><MessageSquare size={12} className="inline mr-1" /> SMS</Link>
            <Link to="/admin/settings" data-testid="nav-settings" className="hidden lg:inline-flex items-center mono-body text-[11px] uppercase tracking-[0.2em] text-nex-chrome hover:text-nex-gold"><KeyRound size={12} className="inline mr-1" /> Settings</Link>
            <Link to="/admin/layout" data-testid="nav-layout" className="hidden lg:inline-flex items-center mono-body text-[11px] uppercase tracking-[0.2em] text-nex-chrome hover:text-nex-gold"><Layers size={12} className="inline mr-1" /> Layout</Link>
            <Link to="/admin/labels" data-testid="nav-labels" className="hidden lg:inline-flex items-center mono-body text-[11px] uppercase tracking-[0.2em] text-nex-chrome hover:text-nex-gold"><Type size={12} className="inline mr-1" /> Labels</Link>
            <span className="mono-body text-[11px] text-nex-chrome hidden xl:inline">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="tick-btn-secondary text-xs !py-2 !px-3"><LogOut size={13} /> <span className="hidden sm:inline">Sign Out</span></button>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-10">
        <Section scope="admin_dashboard" id="kpi_strip">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <div className="mono-label mb-3">{heroKicker}</div>
              <h1 className="serif-head text-4xl md:text-5xl">{heroLabel}</h1>
            </div>
            <div className="grid grid-cols-5 gap-3">
              {[
                { k: "total", label: "Total", cls: "text-nex-silver" },
                { k: "new", label: "New", cls: "text-cyan-300" },
                { k: "accepted", label: "Accepted", cls: "text-nex-gold" },
                { k: "paid", label: "Paid", cls: "text-emerald-300" },
                { k: "rejected", label: "Rejected", cls: "text-red-400" },
              ].map(({ k, label, cls }) => (
                <div key={k} data-testid={`stat-${k}`} className="border border-white/10 p-3 min-w-[70px]">
                  <div className="mono-body text-[9px] uppercase tracking-widest text-nex-muted">{label}</div>
                  <div className={`serif-head text-2xl ${cls}`}>{stats[k]}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section scope="admin_dashboard" id="filters_bar">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex gap-2 flex-wrap">
              {FILTERS.map((f) => (
                <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-[12px] mono-body uppercase tracking-widest border transition-colors ${filter === f ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-chrome hover:border-white/30"}`}>{f}</button>
              ))}
              <button data-testid="filter-archived" onClick={() => setShowArchived(!showArchived)} className={`px-3 py-1.5 text-[12px] mono-body uppercase tracking-widest border transition-colors ${showArchived ? "border-nex-silver text-nex-silver bg-white/5" : "border-white/10 text-nex-muted hover:border-white/30"}`}>
                <Archive size={11} className="inline mr-1" /> Archived
              </button>
            </div>
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted" />
              <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={searchPlaceholder} className="input-industrial pl-9 !py-2 text-sm" />
            </div>
          </div>
        </Section>

        {selected.size > 0 && (
          <Section scope="admin_dashboard" id="bulk_actions">
            <div data-testid="bulk-bar" className="flex items-center justify-between bg-nex-gold/10 border border-nex-gold/40 px-4 py-3 mb-4">
              <div className="mono-body text-[11px] text-nex-silver">{selected.size} selected</div>
              <div className="flex gap-2">
                <button data-testid="bulk-archive" onClick={bulkArchive} className="tick-btn-secondary text-xs !py-1.5 !px-3"><Archive size={13} /> Archive</button>
                <button data-testid="bulk-delete" onClick={bulkDelete} className="tick-btn-secondary text-xs !py-1.5 !px-3 text-red-400 hover:text-red-300"><Trash2 size={13} /> Delete</button>
                <button onClick={() => setSelected(new Set())} className="tick-btn-secondary text-xs !py-1.5 !px-3">Clear</button>
              </div>
            </div>
          </Section>
        )}

        <Section scope="admin_dashboard" id="leads_table">

        {loading ? (
          <div className="py-20 text-center mono-body text-[12px] text-nex-chrome"><Loader2 size={16} className="animate-spin inline mr-2" /> Loading briefs...</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center"><Inbox size={32} className="mx-auto text-nex-muted mb-3" /><div className="mono-body text-[12px] text-nex-chrome">No briefs match — the pipeline is quiet.</div></div>
        ) : (
          <div className="card-industrial overflow-x-auto">
            <div className="min-w-[900px]">
            <div className="grid grid-cols-[36px_2.5fr_2fr_1.5fr_1.5fr_1fr_2fr] mono-body text-[10px] uppercase tracking-widest px-4 py-3 bg-[#0B0F19] border-b border-white/10 text-nex-muted gap-3 items-center">
              <div>
                <input type="checkbox" data-testid="select-all" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} className="accent-nex-gold" />
              </div>
              <div>Business</div>
              <div>Contact</div>
              <button data-testid="sort-score" onClick={() => toggleSort("friction_score")} className="text-left uppercase tracking-widest text-nex-muted hover:text-nex-gold">Engine · Score{sortIcon("friction_score")}</button>
              <button data-testid="sort-date" onClick={() => toggleSort("submitted_at")} className="text-left uppercase tracking-widest text-nex-muted hover:text-nex-gold">Submitted{sortIcon("submitted_at")}</button>
              <div className="text-center">Status</div>
              <div className="text-right">Actions</div>
            </div>
            {filtered.map((b) => {
              const status = b.status || "new";
              const dt = new Date(b.submitted_at);
              const isSel = selected.has(b.id);
              return (
                <div key={b.id} data-testid={`brief-row-${b.id}`} className={`grid grid-cols-[36px_2.5fr_2fr_1.5fr_1.5fr_1fr_2fr] gap-3 px-4 py-4 items-center text-sm border-b border-white/5 last:border-b-0 transition-colors ${isSel ? "bg-nex-gold/5" : "hover:bg-white/[0.02]"} ${b.archived ? "opacity-60" : ""}`}>
                  <div>
                    <input type="checkbox" data-testid={`select-${b.id}`} checked={isSel} onChange={() => toggleSelect(b.id)} className="accent-nex-gold" />
                  </div>
                  <div>
                    <div className="text-nex-silver font-medium flex items-center gap-2">
                      {b.business_name}
                      {b.archived && <Archive size={11} className="text-nex-muted" title="Archived" />}
                    </div>
                    <div className="mono-body text-[11px]">{b.industry} · {b.team_size}</div>
                  </div>
                  <div>
                    <div className="text-nex-silver">{b.full_name}</div>
                    <a href={`mailto:${b.email}`} className="mono-body text-[11px] text-nex-gold hover:underline">{b.email}</a>
                  </div>
                  <div>
                    <div className="text-nex-silver">{b.engine_recommendation}</div>
                    <div className="mono-body text-[11px] gold-text font-bold">{b.friction_score}/100</div>
                  </div>
                  <div className="mono-body text-[11px]">
                    {dt.toLocaleDateString()} <span className="text-nex-muted">·</span> {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-center">
                    <span className={`inline-block px-2 py-1 mono-body text-[9px] uppercase tracking-widest border ${STATUS_COLORS[status] || STATUS_COLORS.new}`}>{status}</span>
                  </div>
                  <div className="text-right flex items-center justify-end gap-1">
                    <Link to={`/admin/brief/${b.id}`} data-testid={`open-brief-${b.id}`} className="inline-flex items-center gap-1 text-nex-gold hover:text-nex-goldbright text-xs font-semibold px-2 py-1">
                      Open <ExternalLink size={11} />
                    </Link>
                    <button onClick={() => setEditing({ ...b, status: b.status || "new" })} data-testid={`edit-brief-${b.id}`} title="Edit" className="p-2 text-nex-chrome hover:text-nex-gold"><Edit3 size={13} /></button>
                    <button onClick={() => archiveOne(b)} data-testid={`archive-brief-${b.id}`} title={b.archived ? "Restore" : "Archive"} className="p-2 text-nex-chrome hover:text-nex-silver">
                      {b.archived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
                    </button>
                    <button onClick={() => deleteOne(b)} data-testid={`delete-brief-${b.id}`} title="Delete" className="p-2 text-nex-chrome hover:text-red-400"><Trash2 size={13} /></button>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}
        </Section>

        {editing && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => !busy && setEditing(null)}>
            <form onSubmit={submitEdit} onClick={(e) => e.stopPropagation()} className="card-industrial corner-tick p-8 w-full max-w-md relative" data-testid="edit-modal">
              <button type="button" onClick={() => !busy && setEditing(null)} className="absolute top-4 right-4 text-nex-muted hover:text-nex-silver"><X size={16} /></button>
              <div className="mono-label mb-2">// EDIT LEAD</div>
              <h2 className="serif-head text-2xl mb-6">{editing.business_name}</h2>
              <div className="space-y-4">
                <div>
                  <label className="mono-label block mb-1.5">Business name</label>
                  <input data-testid="edit-business" value={editing.business_name || ""} onChange={(e) => setEditing({ ...editing, business_name: e.target.value })} className="input-industrial" required />
                </div>
                <div>
                  <label className="mono-label block mb-1.5">Contact name</label>
                  <input data-testid="edit-full-name" value={editing.full_name || ""} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} className="input-industrial" />
                </div>
                <div>
                  <label className="mono-label block mb-1.5">Email</label>
                  <input data-testid="edit-email" type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="input-industrial" />
                </div>
                <div>
                  <label className="mono-label block mb-1.5">Status</label>
                  <select data-testid="edit-status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="input-industrial">
                    {EDITABLE_STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setEditing(null)} disabled={busy} className="tick-btn-secondary flex-1 justify-center">Cancel</button>
                <button type="submit" disabled={busy} data-testid="edit-submit" className="tick-btn-primary flex-1 justify-center disabled:opacity-50">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <><Edit3 size={16} /> Save Changes</>}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
